import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Avatar,
  AvatarGroup,
  Box,
  Button,
  Flex,
  HStack,
  Heading,
  Icon,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Spinner,
  Text,
  VStack,
  useToast
} from "@chakra-ui/react";
import {
  PiSealCheckDuotone as FiCheckCircle,
  PiUserPlusDuotone as FiUserPlus,
  PiCheckCircleDuotone as FiUserCheck,
  PiFileTextDuotone as FiFileText,
  PiCheckSquareDuotone as FiCheckSquare,
  PiLinkDuotone as FiLink,
  PiPencilSimpleDuotone as FiEdit2,
  PiUsersThreeDuotone as FiUsers,
  PiShareNetworkDuotone as FiShare2,
  PiCaretRightBold,
  PiIdentificationCardDuotone,
  PiEnvelopeSimpleDuotone,
  PiHeartDuotone,
  PiScalesDuotone
} from "react-icons/pi";
import { VotingTile } from "../../components/VotingTile";
import { GLASS, SCROLL_FOG, drgba, EASE } from "../../components/glassUi";
import { VerifiedBadge } from "../../components/VerifiedBadge";
import WallPostCard from "../GroupWallView/WallPostCard";
import {
  FollowListUser,
  PublicProfile,
  WallPost,
  getProfileFollowers,
  getProfileFollowing,
  getPublicProfile,
  followUser,
  unfollowUser,
  LOCAL_STOREGE_KEYS
} from "@lubbezposrednio-frontend/core";
import { OrganizerPage, OrganizerShell } from "../OrganizerDashboardView/components/OrganizerShell";

const backendUrl = (path?: string | null) => (path ? `${process.env.REACT_APP_BACKEND_URL}${path}` : undefined);

type ProfileTab = "posty" | "glosowania" | "grupy";
type FollowMode = "followers" | "following";

const EmptyBox: React.FC<{ text: string }> = ({ text }) => (
  <Flex direction="column" align="center" justify="center" py="48px" borderRadius="20px" sx={GLASS}>
    <Flex w="52px" h="52px" borderRadius="15px" bg={drgba("#430098", 0.1)} align="center" justify="center" mb="12px">
      <Icon as={FiUsers} boxSize="24px" color="primary" />
    </Flex>
    <Text color="#6b6480" fontSize="14px" textAlign="center" maxW="360px">{text}</Text>
  </Flex>
);

// ── Prymitywy raila (styl pulpitu) ──
const RailHeading: React.FC<{ icon: React.ElementType; title: string; color?: string; count?: number; onAll?: () => void }> = ({ icon, title, color = "#430098", count, onAll }) => (
  <Flex justify="space-between" align="center" mb="11px" gap="8px">
    <HStack spacing="8px" minW={0}>
      <Icon as={icon} boxSize="18px" color={color} flexShrink={0} />
      <Text fontSize="12px" fontWeight="800" color="#5a5468" textTransform="uppercase" letterSpacing="0.08em" noOfLines={1}>{title}</Text>
      {count !== undefined && <Text fontSize="12px" fontWeight="800" color="#a29bb2">{count}</Text>}
    </HStack>
    {onAll && <Text as="button" onClick={onAll} fontSize="12px" fontWeight="700" color={color} flexShrink={0} _hover={{ textDecoration: "underline" }}>Wszystkie</Text>}
  </Flex>
);
const RailDivider = () => <Box borderTop="1px solid rgba(67,0,152,0.08)" my="20px" />;
const MetaRow: React.FC<{ icon: React.ElementType; color: string; label: string; value: React.ReactNode }> = ({ icon, color, label, value }) => (
  <HStack justify="space-between" spacing="10px">
    <HStack spacing="8px" color="#6b6480" minW={0}><Icon as={icon} boxSize="14px" color={color} flexShrink={0} /><Text fontSize="13px" noOfLines={1}>{label}</Text></HStack>
    <Text fontSize="13px" fontWeight="800" color="#241536" textAlign="right" noOfLines={1} flexShrink={0}>{value}</Text>
  </HStack>
);

export const ProfilePublicView: React.FC = () => {
  const { ref } = useParams<{ ref: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [profile, setProfile] = useState<PublicProfile | null>(null);
  const [posts, setPosts] = useState<WallPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [following, setFollowing] = useState(false);
  const [followers, setFollowers] = useState(0);
  const [followingCount, setFollowingCount] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);
  const [tab, setTab] = useState<ProfileTab>("glosowania");

  // Modal listy obserwujących/obserwowanych
  const [followMode, setFollowMode] = useState<FollowMode | null>(null);
  const [followList, setFollowList] = useState<FollowListUser[] | null>(null);
  const [followTotal, setFollowTotal] = useState(0);

  // Podglądy z awatarami do widgetów w railu.
  const [followersPreview, setFollowersPreview] = useState<FollowListUser[]>([]);
  const [followingPreview, setFollowingPreview] = useState<FollowListUser[]>([]);

  const loggedIn = !!localStorage.getItem(LOCAL_STOREGE_KEYS.ACCESS_TOKEN);

  useEffect(() => {
    if (!ref) return;
    document.title = "Profil";
    setLoading(true);
    setNotFound(false);
    getPublicProfile(ref)
      .then(r => {
        const u = r.data.user;
        setProfile(u);
        setPosts(u.posts ?? []);
        setFollowing(u.followed_by_me);
        setFollowers(u.followers_count);
        setFollowingCount(u.following_count);
        setTab((u.posts ?? []).length > 0 ? "posty" : "glosowania");
        document.title = u.name;
      })
      .catch(e => {
        if (e?.response?.status === 404) setNotFound(true);
      })
      .finally(() => setLoading(false));
    // Podglądy z awatarami do railu (obserwujący/obserwowani).
    getProfileFollowers(ref).then(r => setFollowersPreview(r.data.users.slice(0, 6))).catch(() => undefined);
    getProfileFollowing(ref).then(r => setFollowingPreview(r.data.users.slice(0, 6))).catch(() => undefined);
  }, [ref]);

  const toggleFollow = async () => {
    if (!profile) return;
    if (!loggedIn) {
      toast({ title: "Zaloguj się, aby obserwować", status: "info", isClosable: true });
      navigate("/logowanie");
      return;
    }
    setFollowBusy(true);
    try {
      const r = following ? await unfollowUser(profile.id) : await followUser(profile.id);
      setFollowing(r.data.following);
      setFollowers(r.data.followers_count);
    } catch {
      toast({ title: "Nie udało się zmienić obserwowania", status: "error", isClosable: true });
    } finally {
      setFollowBusy(false);
    }
  };

  const openFollowList = (mode: FollowMode) => {
    if (!ref) return;
    setFollowMode(mode);
    setFollowList(null);
    const fetcher = mode === "followers" ? getProfileFollowers : getProfileFollowing;
    fetcher(ref)
      .then(r => { setFollowList(r.data.users); setFollowTotal(r.data.total); })
      .catch(() => { setFollowList([]); setFollowTotal(0); });
  };

  // ── Stany pomocnicze ──
  const centered = (node: React.ReactNode) =>
    loggedIn ? (
      <OrganizerShell>
        <OrganizerPage>
          <Flex minH="50vh" align="center" justify="center" direction="column" gap="14px">
            {node}
          </Flex>
        </OrganizerPage>
      </OrganizerShell>
    ) : (
      <Flex minH="100vh" align="center" justify="center" direction="column" gap="14px" bg="#FAF8FE" p="20px">
        {node}
      </Flex>
    );

  if (loading) return centered(<Spinner color="primary" size="lg" thickness="3px" />);

  if (notFound || !profile) {
    return centered(
      <VStack spacing="14px">
        <Heading fontSize="22px" color="primary">Nie znaleziono profilu</Heading>
        <Text color="gray.500">Ten profil nie istnieje albo nie jest publiczny.</Text>
        <Button as={Link} to={loggedIn ? "/organizator" : "/"} bg="primary" color="white" borderRadius="12px" fontWeight="700" _hover={{ bg: "#360078" }}>
          {loggedIn ? "Wróć do panelu" : "Strona główna"}
        </Button>
      </VStack>
    );
  }

  const avatarSrc = backendUrl(profile.avatar_url);

  const Stat: React.FC<{ value: number; label: string; onClick?: () => void }> = ({ value, label, onClick }) => (
    <VStack as={onClick ? "button" : "div"} onClick={onClick} spacing="0" align="flex-start" cursor={onClick ? "pointer" : "default"} borderRadius="12px" px="12px" py="6px" transition="all 0.15s" _hover={onClick ? { bg: "rgba(67,0,152,0.05)" } : undefined}>
      <Text fontWeight="900" fontSize="19px" color="primary" lineHeight="1.1">{value}</Text>
      <Text color="gray.400" fontWeight="700" fontSize="12px" textTransform="uppercase" letterSpacing="0.02em">{label}</Text>
    </VStack>
  );

  const actionButton = profile.is_me ? (
    <Button as={Link} to="/organizator/konto" leftIcon={<Icon as={FiEdit2} />} size="sm" variant="outline" borderColor="rgba(67,0,152,0.25)" color="primary" borderRadius="12px" fontWeight="800" px="18px" _hover={{ bg: drgba("#430098", 0.05), textDecoration: "none" }}>
      Edytuj profil
    </Button>
  ) : following ? (
    <Button onClick={toggleFollow} isLoading={followBusy} leftIcon={<Icon as={FiUserCheck} />} size="sm" variant="outline" borderColor="rgba(67,0,152,0.25)" color="primary" borderRadius="12px" fontWeight="800" px="18px" _hover={{ bg: drgba("#430098", 0.05) }}>
      Obserwujesz
    </Button>
  ) : (
    <Button onClick={toggleFollow} isLoading={followBusy} leftIcon={<Icon as={FiUserPlus} />} size="sm" bg="accent" color="white" borderRadius="12px" fontWeight="800" px="20px" boxShadow="0 12px 26px -12px rgba(229,31,68,0.6)" _hover={{ bg: "#c41a3a" }}>
      Obserwuj
    </Button>
  );

  // ── Hero: czysty glass (bez gradientowego covera), awatar w wierszu ──
  const hero = (
    <Box borderRadius="24px" sx={GLASS} p={{ base: "20px", md: "26px" }}>
      <Flex align={{ base: "center", sm: "flex-start" }} gap="18px" direction={{ base: "column", sm: "row" }} textAlign={{ base: "center", sm: "left" }}>
        <Avatar boxSize={{ base: "84px", md: "96px" }} name={profile.name} src={avatarSrc} bg="primary" color="white" flexShrink={0} />
        <Box flex="1" minW={0} w="100%">
          <Flex justify="space-between" align="flex-start" gap="12px" direction={{ base: "column", sm: "row" }}>
            <Box minW={0}>
              <HStack spacing="9px" wrap="wrap" justify={{ base: "center", sm: "flex-start" }}>
                <Heading fontSize={{ base: "23px", md: "27px" }} fontWeight="900" color="#1b1030" noOfLines={2} lineHeight="1.15" letterSpacing="-0.01em">{profile.name}</Heading>
                {profile.verified && (
                  <HStack spacing="4px" bg={drgba("#1d9bf0", 0.12)} color="#1478bd" px="9px" py="3px" borderRadius="full" flexShrink={0}>
                    <Icon as={FiCheckCircle} boxSize="13px" />
                    <Text fontSize="11.5px" fontWeight="800">Zweryfikowany</Text>
                  </HStack>
                )}
              </HStack>
              <HStack spacing="8px" mt="4px" justify={{ base: "center", sm: "flex-start" }} wrap="wrap">
                <Text fontSize="13px" color="#9a94ab" fontWeight="700" fontFamily="mono">{profile.profile_ref}</Text>
                <Box w="3px" h="3px" borderRadius="full" bg="#c9c3d6" />
                <Text fontSize="13px" color="#6b6480" fontWeight="700">{profile.is_organization ? "Organizacja" : "Osoba"}</Text>
              </HStack>
            </Box>
            <Box flexShrink={0} alignSelf={{ base: "center", sm: "flex-start" }}>{actionButton}</Box>
          </Flex>

          {/* Statystyki */}
          <HStack spacing="4px" mt="14px" justify={{ base: "center", sm: "flex-start" }} divider={<Box w="1px" h="26px" bg="rgba(67,0,152,0.10)" alignSelf="center" />}>
            <Stat value={followers} label="Obserwujący" onClick={() => openFollowList("followers")} />
            <Stat value={followingCount} label="Obserwowani" onClick={() => openFollowList("following")} />
            <Stat value={profile.votings.length} label="Głosowania" />
          </HStack>
        </Box>
      </Flex>

      {profile.bio && <Text fontSize="15px" color="#4a4458" mt="16px" lineHeight="1.65">{profile.bio}</Text>}

      {profile.verification_tx && (
        <HStack mt="16px" bg="rgba(24,12,48,0.028)" borderRadius="14px" p="12px 14px" spacing="11px">
          <Flex w="34px" h="34px" borderRadius="10px" bg={drgba("#430098", 0.12)} align="center" justify="center" flexShrink={0}>
            <Icon as={FiLink} color="primary" boxSize="16px" />
          </Flex>
          <Box minW={0}>
            <Text fontSize="12px" color="#8a8397" fontWeight="700">Tożsamość zakotwiczona na blockchainie</Text>
            <Text fontSize="12.5px" color="primary" fontWeight="700" noOfLines={1}>{profile.verification_tx}</Text>
          </Box>
        </HStack>
      )}
    </Box>
  );

  // ── Zakładki treści ──
  const TabBtn: React.FC<{ id: ProfileTab; label: string; icon: React.ElementType; count: number }> = ({ id, label, icon, count }) => {
    const active = tab === id;
    return (
      <Button flex="1" onClick={() => setTab(id)} size="md" borderRadius="12px" fontWeight="800" leftIcon={<Icon as={icon} />} boxShadow="none"
        color={active ? "#7b2ff7" : "#6b6480"} bg={active ? "rgba(123,47,247,0.1)" : "transparent"}
        _hover={{ bg: active ? "rgba(123,47,247,0.16)" : "rgba(24,12,48,0.06)" }}>
        {label}{count > 0 ? ` · ${count}` : ""}
      </Button>
    );
  };

  const main = (
    <VStack align="stretch" spacing="16px">
      <HStack spacing="6px" borderRadius="16px" p="6px" w="100%" sx={GLASS}>
        <TabBtn id="posty" label="Posty" icon={FiFileText} count={posts.length} />
        <TabBtn id="glosowania" label="Głosowania" icon={FiCheckSquare} count={profile.votings.length} />
        <TabBtn id="grupy" label="Grupy" icon={FiUsers} count={profile.groups.length} />
      </HStack>

      {tab === "posty" &&
        (posts.length > 0 ? (
          <VStack align="stretch" spacing="14px">
            {posts.map(p => (
              <WallPostCard
                key={p.id}
                post={p}
                groupId=""
                isModerator={false}
                onChange={updater => setPosts(prev => prev.map(x => (x.id === p.id ? updater(x) : x)))}
                onRemove={() => setPosts(prev => prev.filter(x => x.id !== p.id))}
              />
            ))}
          </VStack>
        ) : (
          <EmptyBox text={loggedIn ? "Brak postów do wyświetlenia." : "Zaloguj się, aby zobaczyć posty tego konta."} />
        ))}

      {tab === "glosowania" &&
        (profile.votings.length > 0 ? (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
            {profile.votings.map(v => (
              <VotingTile key={v.id} id={v.id} title={v.title} status={v.status} startDate={v.start_date} endDate={v.end_date} votersCount={v.voters_count} questionsCount={v.questions_count} organizer={{ name: profile.name, avatarUrl: profile.avatar_url, profileRef: profile.profile_ref }} />
            ))}
          </SimpleGrid>
        ) : (
          <EmptyBox text="To konto nie udostępniło publicznych głosowań." />
        ))}

      {tab === "grupy" &&
        (profile.groups.length > 0 ? (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
            {profile.groups.map(g => (
              <Flex
                key={g.id}
                as={Link}
                to={`/organizator/grupy/${g.id}`}
                direction="column"
                borderRadius="20px"
                p="18px"
                sx={GLASS}
                transition={`transform ${EASE}, box-shadow ${EASE}`}
                _hover={{ transform: "translateY(-3px)", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.7), 0 22px 50px -22px rgba(43,0,90,0.5)", textDecoration: "none" }}
              >
                <HStack spacing="13px" mb="12px">
                  {g.avatar_url ? (
                    <Image src={backendUrl(g.avatar_url)} alt={g.name} w="50px" h="50px" borderRadius="14px" objectFit="cover" flexShrink={0} />
                  ) : (
                    <Flex w="50px" h="50px" align="center" justify="center" bg={drgba("#430098", 0.1)} borderRadius="14px" flexShrink={0}>
                      <Icon as={FiUsers} color="primary" boxSize="22px" />
                    </Flex>
                  )}
                  <Box minW={0}>
                    <Heading fontSize="16px" color="#241536" noOfLines={1} letterSpacing="-0.01em">{g.name}</Heading>
                    <HStack spacing="5px" w="fit-content" bg="rgba(47,206,37,0.12)" px="9px" py="2px" borderRadius="full" mt="5px">
                      <Box w="6px" h="6px" borderRadius="full" bg="#2FCE25" />
                      <Text fontSize="11.5px" fontWeight="700" color="#1f9e18">Publiczna</Text>
                    </HStack>
                  </Box>
                </HStack>
                <HStack spacing="5px" color="gray.400">
                  <Icon as={FiUsers} boxSize="14px" />
                  <Text fontSize="14px" fontWeight="800" color="gray.700">{g.members_count}</Text>
                  <Text fontSize="13px" color="gray.400" fontWeight="600">{g.members_count === 1 ? "osoba" : "osób"}</Text>
                </HStack>
              </Flex>
            ))}
          </SimpleGrid>
        ) : (
          <EmptyBox text="To konto nie udostępniło publicznych grup." />
        ))}
    </VStack>
  );

  const org = profile.org_details;
  const addressLine = org ? [org.address, [org.zip_code, org.city].filter(Boolean).join(" ")].filter(Boolean).join(", ") : "";

  const copyProfileLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/profil/${profile.profile_ref}`);
      toast({ title: "Skopiowano link do profilu", status: "success", duration: 1800, isClosable: true });
    } catch {
      toast({ title: "Nie udało się skopiować linku", status: "error", isClosable: true });
    }
  };

  // Avatar-stack (nakładające się awatary), klikalny → otwiera pełną listę w modalu.
  const avatarStack = (list: FollowListUser[], total: number, onAll: () => void, empty: string) =>
    list.length > 0 ? (
      <Flex as="button" onClick={onAll} align="center" gap="11px" w="100%" py="2px" transition={`transform ${EASE}`} _hover={{ transform: "translateX(2px)" }}>
        <AvatarGroup size="md" max={6} spacing="-14px">
          {list.map(u => (
            <Avatar key={u.id} boxSize="40px" name={u.name} src={backendUrl(u.avatar_url)} bg="primary" color="white" borderColor="white" borderWidth="2px" />
          ))}
        </AvatarGroup>
        {total > list.length && (
          <Flex w="40px" h="40px" borderRadius="full" bg="rgba(24,12,48,0.06)" align="center" justify="center" ml="-14px" border="2px solid white" flexShrink={0}>
            <Text fontSize="12.5px" fontWeight="800" color="#6b6480">+{total - list.length}</Text>
          </Flex>
        )}
      </Flex>
    ) : (
      <Text fontSize="13px" color="#a8a2b6">{empty}</Text>
    );

  const renderGroup = (g: PublicProfile["groups"][number]) => (
    <HStack key={g.id} as={Link} to={`/organizator/grupy/${g.id}`} spacing="10px" py="6px" px="6px" borderRadius="11px" transition={`background ${EASE}`} _hover={{ bg: "rgba(24,12,48,0.04)", textDecoration: "none" }}>
      <Avatar size="sm" boxSize="34px" borderRadius="10px" name={g.name} src={backendUrl(g.avatar_url)} bg={drgba("#430098", 0.1)} color="primary" icon={<Icon as={FiUsers} boxSize="16px" />} />
      <Box minW={0} flex="1">
        <Text fontSize="13.5px" fontWeight="700" color="#2f2a3d" noOfLines={1}>{g.name}</Text>
        <Text fontSize="11.5px" color="#a8a2b6" fontWeight="600">{g.members_count} {g.members_count === 1 ? "osoba" : "osób"}</Text>
      </Box>
      <Icon as={PiCaretRightBold} boxSize="12px" color="#c9c3d6" flexShrink={0} />
    </HStack>
  );

  const rail = (
    <>
      {/* Obserwujący — avatar-stack */}
      <RailHeading icon={FiUsers} title="Obserwujący" color="#7b2ff7" count={followers} onAll={followers > 0 ? () => openFollowList("followers") : undefined} />
      {avatarStack(followersPreview, followers, () => openFollowList("followers"), "Brak publicznych obserwujących.")}

      <RailDivider />

      {/* Obserwowani — avatar-stack */}
      <RailHeading icon={PiHeartDuotone} title="Obserwowani" color="#e51f44" count={followingCount} onAll={followingCount > 0 ? () => openFollowList("following") : undefined} />
      {avatarStack(followingPreview, followingCount, () => openFollowList("following"), "Nikogo nie obserwuje publicznie.")}

      {profile.groups.length > 0 && (
        <>
          <RailDivider />
          <RailHeading icon={FiUsers} title="Grupy" color="#2563eb" count={profile.groups.length} onAll={() => setTab("grupy")} />
          <VStack align="stretch" spacing="1px">{profile.groups.slice(0, 6).map(renderGroup)}</VStack>
        </>
      )}

      <RailDivider />

      {/* O koncie */}
      <RailHeading icon={PiIdentificationCardDuotone} title="O koncie" color="#0d9488" />
      <VStack align="stretch" spacing="11px">
        <MetaRow icon={PiIdentificationCardDuotone} color="#0d9488" label="Typ" value={profile.is_organization ? "Organizacja" : "Osoba"} />
        <MetaRow icon={FiCheckCircle} color={profile.verified ? "#1478bd" : "#a29bb2"} label="Weryfikacja" value={profile.verified ? "Zweryfikowane" : "Brak"} />
        {profile.email && <MetaRow icon={PiEnvelopeSimpleDuotone} color="#7b2ff7" label="E-mail" value={profile.email} />}
        <MetaRow icon={PiScalesDuotone} color="#e51f44" label="Głosowania" value={String(profile.votings.length)} />
      </VStack>

      {org && (
        <>
          <RailDivider />
          <RailHeading icon={FiFileText} title="Dane organizacji" color="#2563eb" />
          <VStack align="stretch" spacing="11px">
            {org.nip && <MetaRow icon={FiFileText} color="#2563eb" label="NIP" value={org.nip} />}
            {org.regon && <MetaRow icon={FiFileText} color="#2563eb" label="REGON" value={org.regon} />}
            {org.krs && <MetaRow icon={FiFileText} color="#2563eb" label="KRS" value={org.krs} />}
            {addressLine && <MetaRow icon={FiFileText} color="#2563eb" label="Adres" value={addressLine} />}
          </VStack>
        </>
      )}

      <RailDivider />

      <Button onClick={copyProfileLink} leftIcon={<Icon as={FiShare2} />} size="sm" w="100%" variant="outline" borderColor="rgba(67,0,152,0.25)" color="primary" borderRadius="12px" fontWeight="800" _hover={{ bg: drgba("#430098", 0.05) }}>
        Udostępnij profil
      </Button>
    </>
  );

  const twoPane = (
    <Flex align="flex-start">
      <Box flex="1" minW={0}>
        <Box w="100%" maxW="1000px" mx="auto" px={{ base: "20px", md: "44px" }} py={{ base: "24px", md: "36px" }}>
          <VStack align="stretch" spacing="16px">
            {hero}
            {main}
          </VStack>

          {/* Rail zwinięty <xl */}
          <Box display={{ base: "block", xl: "none" }} mt="18px" borderRadius="22px" px="18px" py="20px" sx={GLASS}>
            {rail}
          </Box>
        </Box>
      </Box>

      {/* ── GŁÓWNY PRAWY RAIL — pływający, pełnowysokościowy ── */}
      <Box as="aside" display={{ base: "none", xl: "block" }} w="384px" flexShrink={0} alignSelf="stretch">
        <Box position="sticky" top="0" h="100vh" p="12px">
          <Box position="relative" h="100%" borderRadius="26px" overflow="hidden" sx={GLASS}>
            <Box position="absolute" inset={0} pointerEvents="none" sx={{ background: "radial-gradient(58% 40% at 50% 20%, rgba(124,58,237,0.14), transparent 72%)" }} />
            <VStack position="relative" align="stretch" spacing="0" h="100%" px="16px" py="20px" sx={{ ...SCROLL_FOG, maskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)", WebkitMaskImage: "linear-gradient(to bottom, #000 0, #000 calc(100% - 24px), transparent 100%)" }}>
              {rail}
            </VStack>
          </Box>
        </Box>
      </Box>
    </Flex>
  );

  const followModal = (
    <Modal isOpen={followMode !== null} onClose={() => setFollowMode(null)} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent borderRadius="18px">
        <ModalHeader color="primary" fontWeight="800">
          {followMode === "followers" ? "Obserwujący" : "Obserwowani"}
          {followList && <Text as="span" fontSize="14px" color="gray.400" fontWeight="600"> · {followTotal}</Text>}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody pb="20px">
          {!followList ? (
            <Flex justify="center" py="30px"><Spinner color="primary" /></Flex>
          ) : followList.length === 0 ? (
            <Text color="gray.400" textAlign="center" py="20px">Brak publicznych kont do pokazania.</Text>
          ) : (
            <VStack align="stretch" spacing="2px">
              {followTotal > followList.length && (
                <Text fontSize="12px" color="gray.400" mb="6px">Pokazujemy tylko konta z profilem publicznym ({followList.length} z {followTotal}).</Text>
              )}
              {followList.map(u => (
                <HStack key={u.id} as={Link} to={`/profil/${u.profile_ref}`} onClick={() => setFollowMode(null)} spacing="11px" p="9px" borderRadius="12px" _hover={{ bg: "#FAF8FE" }}>
                  <Avatar size="sm" name={u.name} src={backendUrl(u.avatar_url)} bg="primary" color="white" />
                  <Box minW={0} flex="1">
                    <HStack spacing="5px" minW={0}>
                      <Text fontSize="14px" fontWeight="700" color="primary" noOfLines={1}>{u.name}</Text>
                      {u.verified && <VerifiedBadge size="13px" />}
                    </HStack>
                    <Text fontSize="11.5px" color="gray.400" noOfLines={1}>{u.profile_ref}{u.is_organization ? " · Organizacja" : ""}</Text>
                  </Box>
                </HStack>
              ))}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );

  // Zalogowany → osadzone w panelu (shell z lewym menu); wylogowany → samodzielna strona publiczna.
  if (loggedIn) {
    return (
      <OrganizerShell>
        {twoPane}
        {followModal}
      </OrganizerShell>
    );
  }

  return (
    <Box minH="100vh" sx={{ background: "linear-gradient(160deg, #F6F6F7 0%, #F0F0F2 55%, #EAEAEC 100%)" }}>
      {twoPane}
      {followModal}
    </Box>
  );
};

export default ProfilePublicView;
